import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from './AppShell';
import { RouteErrorPage } from './RouteErrorPage';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { Spinner } from '../components/ui/Spinner';
import { HomePage } from '../features/home/HomePage';
import { LoginPage } from '../features/auth/LoginPage';
import { RegisterPage } from '../features/auth/RegisterPage';
import { ForgotPasswordPage, ResetPasswordPage } from '../features/auth/PasswordPages';
import {
  CheckYourEmailPage,
  ConfirmEmailPage,
  PendingApprovalPage,
} from '../features/auth/StatusPages';
import { BookPage } from '../features/booking/BookPage';
import { MyBookingsPage } from '../features/myBookings/MyBookingsPage';
import { MyPaymentsPage } from '../features/myBookings/MyPaymentsPage';

// The coach core above stays in the entry chunk (it's the Lighthouse-audited path). Everything
// below is code-split: admin/dev surfaces most coaches never load, plus the two heavy leaves —
// the dnd-kit scheduler and the qrcode MFA enrolment.
const MfaSetupPage = lazy(() =>
  import('../features/auth/MfaSetupPage').then((m) => ({ default: m.MfaSetupPage })),
);
const ConfirmPitchPage = lazy(() =>
  import('../features/matchday/ConfirmPitchPage').then((m) => ({ default: m.ConfirmPitchPage })),
);
// Reached from the renewal email, not the nav — lazy so the common My bookings path stays small.
const ExtendBlockPage = lazy(() =>
  import('../features/myBookings/ExtendBlockPage').then((m) => ({ default: m.ExtendBlockPage })),
);
const SchedulerPage = lazy(() =>
  import('../features/scheduler/SchedulerPage').then((m) => ({ default: m.SchedulerPage })),
);
const DashboardPage = lazy(() =>
  import('../features/admin/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const RequestsPage = lazy(() =>
  import('../features/admin/RequestsPage').then((m) => ({ default: m.RequestsPage })),
);
const UsersPage = lazy(() =>
  import('../features/admin/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const TeamsPage = lazy(() =>
  import('../features/admin/TeamsPage').then((m) => ({ default: m.TeamsPage })),
);
const VenuesPage = lazy(() =>
  import('../features/admin/VenuesPage').then((m) => ({ default: m.VenuesPage })),
);
const MatchdaysPage = lazy(() =>
  import('../features/admin/MatchdaysPage').then((m) => ({ default: m.MatchdaysPage })),
);
const PricingPage = lazy(() =>
  import('../features/admin/PricingPage').then((m) => ({ default: m.PricingPage })),
);
const PaymentsPage = lazy(() =>
  import('../features/admin/PaymentsPage').then((m) => ({ default: m.PaymentsPage })),
);
const SettingsLayout = lazy(() =>
  import('../features/dev/SettingsLayout').then((m) => ({ default: m.SettingsLayout })),
);
const ClubSettingsPage = lazy(() =>
  import('../features/dev/ClubSettingsPage').then((m) => ({ default: m.ClubSettingsPage })),
);
const IntegrationsPage = lazy(() =>
  import('../features/dev/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })),
);
const FeaturesPage = lazy(() =>
  import('../features/dev/FeaturesPage').then((m) => ({ default: m.FeaturesPage })),
);
const MatchdaySettingsPage = lazy(() =>
  import('../features/dev/MatchdaySettingsPage').then((m) => ({ default: m.MatchdaySettingsPage })),
);
const BookingSettingsPage = lazy(() =>
  import('../features/dev/BookingSettingsPage').then((m) => ({ default: m.BookingSettingsPage })),
);
const TemplatesPage = lazy(() =>
  import('../features/dev/TemplatesPage').then((m) => ({ default: m.TemplatesPage })),
);
const EmailLogPage = lazy(() =>
  import('../features/dev/EmailLogPage').then((m) => ({ default: m.EmailLogPage })),
);
const AuditLogPage = lazy(() =>
  import('../features/dev/AuditLogPage').then((m) => ({ default: m.AuditLogPage })),
);

function suspend(Page: ComponentType): ReactNode {
  return (
    <Suspense fallback={<Spinner center />}>
      <Page />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    // Pathless root so ONE errorElement catches everything: unmatched URLs (404), lazy chunks
    // that fail to load (stale tab after a deploy / portal down), and render errors. Without it
    // React Router shows its raw developer error page.
    errorElement: <RouteErrorPage />,
    children: [
      // Landing and login stand alone (full-viewport shell, no app chrome — cc8-portal style).
      // Signed-in users never see '/': HomePage bounces them to their working surface.
      { path: '/', element: <HomePage /> },
      { path: '/login', element: <LoginPage /> },
      {
        element: <AppShell />,
        children: [
          // Public auth surface
          { path: '/register', element: <RegisterPage /> },
          { path: '/confirm-email', element: <ConfirmEmailPage /> },
          { path: '/check-your-email', element: <CheckYourEmailPage /> },
          { path: '/pending-approval', element: <PendingApprovalPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
          { path: '/reset-password', element: <ResetPasswordPage /> },
          { path: '/confirm-pitch', element: suspend(ConfirmPitchPage) },

          // Signed-in surface
          {
            path: '/mfa/setup',
            element: <ProtectedRoute>{suspend(MfaSetupPage)}</ProtectedRoute>,
          },
          {
            path: '/book',
            element: (
              <ProtectedRoute>
                <BookPage />
              </ProtectedRoute>
            ),
          },
          {
            path: '/my-bookings',
            element: (
              <ProtectedRoute>
                <MyBookingsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: '/my-bookings/training/:allocationId/extend',
            element: <ProtectedRoute>{suspend(ExtendBlockPage)}</ProtectedRoute>,
          },
          {
            path: '/my-payments',
            element: (
              <ProtectedRoute>
                <MyPaymentsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: '/admin',
            element: <ProtectedRoute minRole="Admin">{suspend(DashboardPage)}</ProtectedRoute>,
          },
          {
            path: '/admin/schedule',
            element: <ProtectedRoute minRole="Admin">{suspend(SchedulerPage)}</ProtectedRoute>,
          },
          {
            path: '/admin/matchdays',
            element: <ProtectedRoute minRole="Admin">{suspend(MatchdaysPage)}</ProtectedRoute>,
          },
          {
            path: '/admin/requests',
            element: <ProtectedRoute minRole="Admin">{suspend(RequestsPage)}</ProtectedRoute>,
          },
          {
            path: '/admin/users',
            element: <ProtectedRoute minRole="Admin">{suspend(UsersPage)}</ProtectedRoute>,
          },
          {
            path: '/admin/teams',
            element: <ProtectedRoute minRole="Admin">{suspend(TeamsPage)}</ProtectedRoute>,
          },
          {
            path: '/admin/venues',
            element: <ProtectedRoute minRole="Admin">{suspend(VenuesPage)}</ProtectedRoute>,
          },
          {
            path: '/admin/pricing',
            element: <ProtectedRoute minRole="Admin">{suspend(PricingPage)}</ProtectedRoute>,
          },
          {
            path: '/admin/payments',
            element: <ProtectedRoute minRole="Admin">{suspend(PaymentsPage)}</ProtectedRoute>,
          },
          {
            path: '/dev/settings',
            element: <ProtectedRoute minRole="Developer">{suspend(SettingsLayout)}</ProtectedRoute>,
            children: [
              { index: true, element: <Navigate to="club" replace /> },
              { path: 'club', element: suspend(ClubSettingsPage) },
              { path: 'integrations', element: suspend(IntegrationsPage) },
              { path: 'features', element: suspend(FeaturesPage) },
              { path: 'matchday', element: suspend(MatchdaySettingsPage) },
              { path: 'booking', element: suspend(BookingSettingsPage) },
              { path: 'templates', element: suspend(TemplatesPage) },
              { path: 'emails', element: suspend(EmailLogPage) },
              { path: 'audit', element: suspend(AuditLogPage) },
            ],
          },
        ],
      },
    ],
  },
]);
